<script lang="ts" setup>
  import {
    ref,
    reactive,
    provide,
    onMounted,
    onBeforeUnmount,
    computed,
    useTemplateRef,
    watchEffect,
    watch
  } from 'vue'
  import GeneratorHistory from './components/GeneratorHistory.vue'
  import CheckedLists from './components/CheckedLists.vue'
  import UpdataLocalImage from '../AppUploadLocalImage/index.vue'
  import type { Image } from '@/types/image'
  import type { ImageItem } from '../AppUploadLocalImage/index.vue'
  import _fetch from '@/utils/fetch'
  import AppLoading from '@/components/AppLoading.vue'
  import MessageService from '@/components/AppMessage/message'
  import ToastService from '@/components/AppToast/toast'
  import { lockScroll, unlockScroll } from '@/utils/scrollLock'
  import trackEvent from '@/track/trackEvent'
  import { getCurrentPageName } from '@/utils/common'
  import creatorBannerImg from '@/assets/images/about/creator_banner_img.png'
  import { useUserStore } from '@zaohaowu/website-common/stores/userStore'
  import AppImage from '@/components/AppImage.vue'
  import getParamsFromUrl from '@/utils/shareSource'
  import { toRaw } from 'vue'

  interface SelectTopicParams {
    topicId: string
    title: string
    equleNum: number
    want: number
    key?: string
    canPost?: boolean
    activityType: number
    allowPostWantByTopicIds: []
  }

  interface ConfigSettings {
    hidePrompt: boolean
    associateOriginalImage: boolean //是否关联原图
  }
  import { filterInvalidMentionList } from '@/utils/filterInvalidMention'
  import type { PostDialogConfig } from '@/hooks/usePostDialog'

  interface postChallenge {
    title: string
    description: string
    file?: File | null
    checkedImages: Map<string, Image>
    topicId: string
    topicTitle: string
    associateOriginalImage?: boolean
  }

  interface PromptResultInstance extends HTMLDivElement {
    triggerLoading(flag: boolean): void
    clearChecked(): void
  }

  interface Challenge {
    title?: string
    uniqueTopicId?: string
    banners?: { id: string; image: string; sidebarImage: string }[]
  }

  const props = defineProps<PostDialogConfig>()

  const pageName = getCurrentPageName()
  const emit = defineEmits(['close', 'success'])
  const challengeId = ref(props.challengeId)
  const hasHistory = props.hasHistory
  const checkedLength = props.checkedLength
  const wantable = ref(props.wantable)
  const tabType = ref<'history' | 'loacl'>(props.tabType || 'history')
  const step = ref(1)
  const resultRef = ref<PromptResultInstance | null>(null)
  const uploadRef = ref<any>(null)
  const percent = ref(1)
  const activitType = ref(0)
  const uploadLoading = ref(false)
  const checkedListsRef = useTemplateRef<InstanceType<typeof CheckedLists>>('checkedListsRef')
  const sourceImgInfo = ref()
  const checkCanPost = ref(false)

  provide('challengeId', challengeId)
  const $message = MessageService
  const $toast = ToastService
  provide('$toast', $toast) // 提供 toast 服务给子组件
  provide('hasHistory', hasHistory)
  provide('checkedMaxLength', checkedLength)

  const postData: postChallenge = reactive({
    title: '',
    description: '',
    file: null,
    checkedImages: new Map(),
    topicId: '',
    topicTitle: ''
  })

  // 根据数据是否包含视频来判断当前发布类型
  const postType = computed(() => {
    if (postData.checkedImages && typeof postData.checkedImages.forEach === 'function') {
      let hasVideo = false
      postData.checkedImages.forEach((item: any) => {
        if (item && item.target === 'video') {
          hasVideo = true
        }
      })
      return hasVideo ? 'video' : 'image'
    }
    return 'image'
  })

  const userStore = useUserStore()
  let isPGCUser =
    userStore.isCreator() || userStore.user?.showPostLocalUpload === true
      ? true
      : getParamsFromUrl('userType') === 'creator'
        ? true
        : false

  // 检查用户是否有本地上传权限
  const hasLocalUploadPermission = computed(() => {
    return userStore.user?.showPostLocalUpload === true
  })

  const showPromptSetting = ref(false)

  const initView = () => {
    if (props.scene === 'challenge' || props.scene === 'normalChallenge') {
      tabType.value = 'loacl'
    } else {
      tabType.value = props.tabType || 'history'
    }

    if (props.checkedImages?.length) {
      wantable.value = props.checkedImages[0].wantableTopics.length > 0
      step.value = 2
      if (props.postType === 'video') {
        const video = props.checkedImages[0]
        postData.checkedImages.set(video.url, toRaw(video))
        postData.checkedImages.set(
          getOriginImagePath(toRaw(video)),
          getOriginImageInfo(toRaw(video))
        )
      } else {
        props.checkedImages.forEach((item: Image) => {
          postData.checkedImages.set(item.url, item)
        })
      }
    }
  }

  const onInput = (obj: { type: string; value: string }) => {
    if (obj.type === 'title') {
      postData.title = obj.value
    } else {
      postData.description = obj.value
    }
  }

  const onCheckedMore = () => {
    step.value = 1
  }

  const onBack = () => {
    step.value = 1
    postData.title = ''
    postData.description = ''
  }

  const nextStep = () => {
    if (postData.checkedImages.size) {
      step.value = 2
    }
  }
  const suiting = ref(false)

  const getPostApiUrl = (): string => {
    return '/aigc/api/topic/content/general' //这个接口可以不传topicId,后端会给一个默认值
  }

  const postStep = async () => {
    if (tabType.value === 'history') {
      if (disableBtn.value) return
    }
    let type = tabType.value === 'loacl' ? 2 : 1
    suiting.value = true
    const valuesArray = Array.from(postData.checkedImages.values())
    const firstImage = valuesArray.splice(0, 1)
    // 本地图片生成的视频
    const videoLocalImageUrl =
      postType.value === 'video' &&
      postData.associateOriginalImage &&
      !firstImage[0].sourceImageInfo?.itemId
        ? firstImage[0].firstFrameUrl
        : undefined

    const _mentionList = checkedListsRef.value?.getMentionList() ?? []
    const mentionList = filterInvalidMentionList(_mentionList, postData.description)
    const hidePrompt = checkedListsRef.value?.getHidePrompt() ? 1 : 0
    const response = await _fetch(getPostApiUrl(), {
      method: 'POST',
      body: JSON.stringify({
        topicId: props.challengeId || postData.topicId,
        title: postData.title,
        description: postData.description,
        posterImage: firstImage,
        contentImage: valuesArray,
        hiddenPrompt: hidePrompt,
        showCustomizeBuyButton: checkedListsRef.value?.getEnablePurchase() === false ? 0 : 1,
        videoLocalImageUrl,
        type: type,
        mentionUser: mentionList.map(item => ({
          userId: item.userId,
          startIndex: item.startIndex,
          endIndex: item.startIndex + item.length
        }))
      })
    })
    let res = await response.json()
    suiting.value = false
    if (res.code === 200) {
      const activeTopic = challenges.value
        ? challenges.value.filter(item => item.uniqueTopicId === postData.topicId)
        : []
      const topicTitle = activeTopic.length ? activeTopic[0].title : ''
      props.postSuccess({
        posterImage: firstImage,
        topicId: postData.topicId || props.challengeId,
        topicTitle: topicTitle || postData.topicTitle || props.title,
        id: res.data.createId,
        popUpType: res.data?.popUpType || 0, // 1: 限时挑战投稿成功，并且在前xx名，0: 普通投稿成功
        title: postData.title,
        description: postData.description,
        createId: res.data.createId,
        wantable: wantable.value ? 1 : 0,
        type: res.data.contentType
      })
      emit('close')
      checkedListsRef.value?.resetMentionList()
      if (postType.value !== 'video') {
        trackEvent.track('publish', {
          enter_method: tabType.value === 'loacl' ? 'upload' : props.fromWhere,
          page_name: pageName,
          challenge_id: props.challengeId || postData.topicId
        })
      }
      localStorage.setItem('postHidePrompt', String(hidePrompt))
      return
    }
    $message &&
      $message({
        message: `${res.msg}`,
        type: 'error'
      })
  }

  const close = () => {
    if (step.value === 1 && percent.value < 100 && percent.value > 1) {
      $message &&
        $message({
          type: 'error',
          title: '确定退出吗？',
          message: '你的创作内容将会保存在你个人资料的创作历史记录中。',
          timer: 0,
          config: {
            showClose: false,
            showIcon: false,
            showCancel: true,
            showConfirm: true
          },
          onConfirm() {
            emit('close')
          }
        })
    } else if (step.value === 2 || postData.checkedImages.size > 0) {
      $message &&
        $message({
          type: 'error',
          title: '放弃发布吗？',
          message: '如果现在退出，你所编辑的内容将不会被保存。',
          timer: 0,
          config: {
            showClose: false,
            showIcon: false,
            showCancel: true,
            showConfirm: true
          },
          onConfirm() {
            emit('close')
          }
        })
    } else {
      emit('close')
    }
  }

  const unselect = () => {
    postData.checkedImages.clear()
    resultRef.value && resultRef.value.clearChecked()
  }

  const changeTab = (type: 'history' | 'loacl') => {
    if (type === tabType.value) return
    if (type === 'loacl') {
      trackEvent.track('click_upload_entrance')
    }
    if (postData.checkedImages.size > 0) {
      $message &&
        $message({
          type: 'error',
          title: '放弃所有已选内容',
          message: '你正在尝试切换模式，这将清除所有已选内容',
          timer: 0,
          config: {
            showClose: false,
            showIcon: false,
            showCancel: true,
            showConfirm: true
          },
          onConfirm() {
            unselect()
            tabType.value = type
          }
        })
    } else {
      tabType.value = type
    }
  }

  const challenges = ref<Challenge[]>([])
  const getChallenges = async () => {
    const response = await _fetch('/aigc/api/topic/selection', {
      method: 'GET',
      params: {
        page: 1,
        pageSize: 1000,
        showWantable: 1
      }
    })
    const res = await response.json()
    if (res.code === 200) {
      challenges.value = res.data.dataList
    }
  }

  const togglePromptSetting = () => {
    showPromptSetting.value = false
  }

  const wantedNum = ref(0)
  const selectHandle = ({
    topicId,
    title,
    equleNum,
    want,
    key,
    activityType,
    canPost
  }: SelectTopicParams) => {
    postData.topicId = topicId
    postData.topicTitle = title
    wantedNum.value = equleNum
    wantable.value = want === 1
    challengeId.value = topicId
    activitType.value = activityType
    if (canPost !== undefined) {
      checkCanPost.value = canPost
    }
    if (key) {
      postData.checkedImages.delete(key)
      uploadRef.value?.deleteImageByUrl(key)
    }
  }

  const getOriginImageInfo = (videoInfo: any) => {
    const sourcePath = getOriginImagePath(videoInfo)
    return {
      ...videoInfo.sourceImageInfo,
      target: 'image',
      uniqueTopicId: videoInfo.uniqueTopicId,
      imageResize512Url: sourcePath,
      url: sourcePath
    }
  }
  const getOriginImagePath = (videoInfo: any): string => {
    return videoInfo.sourceImageInfo?.itemId
      ? videoInfo.sourceImageInfo.sourceImagePath || ''
      : videoInfo.firstFrameUrl || ''
  }

  const disableBtn = computed(() => {
    if (tabType.value === 'loacl') {
      return wantable.value && activitType.value === 1
    }

    if (postType.value === 'video') {
      return wantable.value && (postData.checkedImages.size > 2 || !checkCanPost.value)
    }
    return (
      wantable.value &&
      (wantedNum.value !== 1 || postData.checkedImages.size > 1 || !checkCanPost.value)
    )
  })

  const uploadImageChange = (images: ImageItem[]) => {
    let loading = false
    postData.checkedImages.clear()
    images.forEach((item: ImageItem) => {
      // 只添加非 loading 状态的图片到选中列表
      if (!item.loading && item.url) {
        // 转换为 Image 类型
        const imageData: any = {
          ...item,
          localKey: undefined,
          localUrl: undefined
        }
        postData.checkedImages.set(item.url, imageData)
      }
      if (item.loading) {
        loading = true
      }
    })
    uploadLoading.value = loading
  }

  onMounted(() => {
    lockScroll()
    getChallenges()
    initView()
  })

  const onChecked = (obj: { item: any; checked: boolean }) => {
    const { item, checked } = obj
    if (checked) {
      // 如果选中的是一个视频，并且当前没有数据源，是一个切换Tab的场景，需要从新赋值postData.checkedImages信息
      if (item.target === 'video') {
        if (postData.checkedImages.size === 0) {
          const sourcePath = getOriginImagePath(item)
          postData.checkedImages.set(item.url, item)
          postData.checkedImages.set(sourcePath, getOriginImageInfo(item))
        }
      } else {
        postData.checkedImages.set(item.url, item)
      }
    } else {
      postData.checkedImages.delete(item?.url ?? '')
      // 如果是视频还要把原图数据删除
      if (item.target === 'video') {
        postData.checkedImages.delete(item.sourceImageInfo.sourceImagePath ?? '')
      }
    }
  }

  // 必须等原图信息赋值后，再根据是否关联原图配置，增删数据源
  watch(
    [() => postData.associateOriginalImage],
    () => {
      if (postType.value !== 'video') {
        return
      }
      const videoInfo = Array.from(postData.checkedImages.values())[0]
      if (!videoInfo) {
        return
      }
      const sourcePath = getOriginImagePath(videoInfo)
      //根据是否关联原图配置，增删数据源
      if (postData.associateOriginalImage && postData.checkedImages.size === 1) {
        // 显示原图
        const formatSourceInfo = getOriginImageInfo(videoInfo)
        postData.checkedImages.set(sourcePath, formatSourceInfo)
      } else if (!postData.associateOriginalImage && postData.checkedImages.size === 2) {
        // 删除原图
        postData.checkedImages.delete(sourcePath)
      }
    },
    {
      immediate: true
    }
  )

  const clickUnlock = () => {
    let origin = window.location.origin
    window.location.href = origin + '/author/onboarding'
  }
  const nextStepEvent = () => {
    if (uploadLoading.value) {
      return
    }
    if (postData.checkedImages.size) {
      step.value = 2
    }
  }

  onBeforeUnmount(() => {
    unlockScroll()
  })

  // 关联原图和隐藏描述词设置变更触发此函数
  const backConfigSettings = (configs: ConfigSettings) => {
    postData.associateOriginalImage = configs.associateOriginalImage
  }
</script>
<template>
  <main class="detail-mask-container">
    <div
      class="detail-container"
      :class="{ 'post-detail-container': step === 2 }"
      id="post_dialog"
      @click="
        event => {
          event.stopPropagation()
          togglePromptSetting()
        }
      "
    >
      <a class="detail-close" @click.stop="close">
        <span class="hitems-close"></span>
      </a>

      <div class="post-content">
        <div v-show="step === 1">
          <div class="post-tabs">
            <a
              v-if="hasHistory"
              :class="['post-tab', 'font-bold', { active: tabType === 'history' }]"
              @click="changeTab('history')"
            >
              历史作品发布
            </a>
            <a
              :class="['post-tab', 'font-bold', { active: tabType === 'loacl' }]"
              @click="changeTab('loacl')"
            >
              <span
                :class="isPGCUser ? 'hitems-jiesuo' : 'hitems-ic_lock'"
                v-if="props.scene === 'home' || props.scene === 'normalChallenge'"
              ></span>
              本地上传并发布
            </a>
          </div>

          <div v-if="tabType === 'history'">
            <GeneratorHistory
              v-model="postData.checkedImages"
              :checkedLength="postData.checkedImages.size"
              :topicId="postData.topicId"
              :wantable="wantable || false"
              @checked="
                data => {
                  onChecked(data)
                }
              "
              @clearCheckedItems="postData.checkedImages.clear()"
            />
            <div class="post-footer">
              <div></div>
              <a
                :class="[
                  'post-btn',
                  'post-btn-submit',
                  'font-bold',
                  { disabled: !postData.checkedImages.size }
                ]"
                @click="nextStep"
              >
                确定
              </a>
            </div>
          </div>

          <div v-if="tabType === 'loacl'">
            <UpdataLocalImage
              v-if="props.scene === 'challenge' || hasLocalUploadPermission"
              ref="uploadRef"
              :maxCount="9"
              :maxSize="20"
              accept="image/jpeg,image/png,image/jpg,image/webp"
              :disabled="false"
              uploadUrl="/aigc/api/generate/generate-image"
              @changeEvent="uploadImageChange"
            />
            <div class="creator-banner-img-wrapper" v-else>
              <div class="creator-banner-img-box">
                <AppImage :src="creatorBannerImg" alt="" />
                <div class="creator-banner-img-btn">
                  <a :class="['post-btn', 'post-btn-submit', 'font-bold']" @click="clickUnlock">
                    立刻入驻
                  </a>
                </div>
              </div>
            </div>

            <div class="post-footer" v-if="props.scene === 'challenge' || hasLocalUploadPermission">
              <div class="post-footer-text">
                <p v-if="postData.checkedImages.size > 0">
                  图片支持 JPG/PNG/JPEG/WEBP，需小于 20 MB，已上传（{{
                    postData.checkedImages.size
                  }}/9）
                </p>
              </div>
              <a
                :class="[
                  'post-btn',
                  'post-btn-submit',
                  'font-bold',
                  { disabled: !postData.checkedImages.size || uploadLoading }
                ]"
                @click="nextStepEvent"
              >
                确定
              </a>
            </div>
          </div>
        </div>

        <CheckedLists
          v-show="step === 2"
          ref="checkedListsRef"
          v-model="postData.checkedImages"
          :challenges="challenges"
          :title="props.title"
          :titleMsg="postData.title"
          :descriptionMsg="postData.description"
          :topicIdMsg="postData.topicId || props.challengeId"
          :tabType="tabType"
          :isShow="step === 2"
          :hasNotBack="props.checkedImages && props.checkedImages.length > 0"
          @input="onInput"
          @select="selectHandle"
          @checkedMore="onCheckedMore"
          @back="onBack"
          @backConfigSettings="backConfigSettings"
        />
      </div>

      <div class="post-footer" v-if="step === 2">
        <div></div>
        <span class="post-btn" v-if="suiting">
          <AppLoading />
        </span>
        <a
          v-else
          :class="[
            'post-btn',
            'post-btn-submit',
            'font-bold',
            {
              disabled: disableBtn
            }
          ]"
          @click="postStep"
        >
          发布
          <div
            v-if="tabType !== 'loacl' && postData.topicId && wantedNum === 0 && wantable"
            class="prompt prompt1"
          >
            <img class="union" src="@/assets/images/Union.png" />
            <span>请添加一张符合「想要」挑战主题的图片</span>
          </div>
          <div
            v-if="
              tabType !== 'loacl' &&
              postType !== 'video' &&
              postData.topicId &&
              wantedNum > 1 &&
              wantable
            "
            class="prompt"
          >
            <img class="union" src="@/assets/images/Union.png" />
            <span>「想要」挑战只支持发布1张图片</span>
          </div>
        </a>
      </div>
    </div>
  </main>
</template>
<style lang="stylus" scoped>
  @import './AppPostChallenge.styl';
</style>
